import { Link } from "react-router-dom";
import {
  Phone,
  MessageSquareText,
  ArrowRight,
  ShieldCheck,
  Wrench,
  Truck,
  PhoneCall,
  Star,
  MapPin,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { ServiceCard } from "@/components/common/ServiceCard";
import { BrandCard } from "@/components/common/BrandCard";
import {
  business,
  heroImages,
  services,
  brands,
  serviceRegions,
  faqs,
  testimonials,
  trustPoints,
} from "@/data/site";

const STAR_KEYS = ["s1", "s2", "s3", "s4", "s5"];

const steps = [
  {
    icon: PhoneCall,
    title: "Call or Text",
    body: "Reach John directly at 814-410-5822. Describe the issue or the equipment you need \u2014 no phone trees, no runaround.",
  },
  {
    icon: Truck,
    title: "We Come to You",
    body: "We deliver, set up and assess equipment right in your home across our service area.",
  },
  {
    icon: Wrench,
    title: "Fixed & Followed Up",
    body: "Powered or non-powered, we repair it and make sure it keeps working the way it should.",
  },
];

export default function Home() {
  return (
    <div>
      {/* ===== HERO ===== */}
      <section className="relative isolate overflow-hidden bg-brand-deep">
        <img
          src={heroImages.main}
          alt="Senior receiving help with a mobility device"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative mx-auto max-w-7xl py-24 container-px sm:py-32 lg:py-40">
          <div className="max-w-2xl">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-4 py-1.5 text-sm font-semibold text-white">
                <ShieldCheck className="h-4 w-4 text-accent" /> Veteran Owned &amp; Operated
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 text-4xl font-bold leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                Medical mobility equipment,{" "}
                <span className="text-primary">delivered &amp; repaired</span> by someone who shows up.
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/75">
                {business.legalName} keeps you moving with in-home delivery, setup and repair of
                powered and non-powered mobility devices &mdash; backed by the brands you trust.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Button asChild variant="brand" size="xl">
                  <a href={business.phoneHref}>
                    <Phone className="h-5 w-5" /> Call {business.phone}
                  </a>
                </Button>
                <Button asChild variant="glass" size="xl">
                  <a href={business.smsHref}>
                    <MessageSquareText className="h-5 w-5" /> Text Us
                  </a>
                </Button>
              </div>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/70">
                {["Powered & non-powered", "In-home service", "PA Registered & Insured", "Serving Veterans & the VA"].map((t) => (
                  <span key={t} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" /> {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== VA / SCOOTAROUND NOTICE ===== */}
      <section className="bg-accent text-accent-foreground">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-center gap-2 py-3 text-center container-px sm:flex-row sm:gap-3">
          <span className="flex items-center gap-2 text-sm font-medium">
            <ShieldCheck className="h-4 w-4" /> Are you a Veteran? You can also connect directly through the VA.
          </span>
          <a
            href="https://scootaround.com/en/va"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-bold underline underline-offset-4 transition-opacity hover:opacity-90"
          >
            Connect via Scootaround <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* ===== TRUST BAR ===== */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px overflow-hidden lg:grid-cols-4">
          {trustPoints.map((tp) => (
            <div key={tp.label} className="bg-card px-5 py-7 text-center sm:px-8">
              <p className="font-display text-lg font-bold text-foreground sm:text-xl">{tp.label}</p>
              <p className="mt-1 text-sm text-muted-foreground">{tp.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="section-py">
        <div className="mx-auto max-w-7xl container-px">
          <SectionHeading
            eyebrow="What we do"
            title="Mobility services for every part of your home"
            description="From the chair you relax in to the stairs you climb, we deliver, install and repair the equipment that keeps you independent."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 4) * 0.06}>
                <ServiceCard service={s} />
              </Reveal>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Button asChild variant="outlineDark" size="lg">
              <Link to="/services">
                See all services <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="bg-muted section-py">
        <div className="mx-auto max-w-7xl container-px">
          <SectionHeading
            eyebrow="How it works"
            title="Help that's simple to reach"
            description="No call centers. No complicated process. Just a Veteran who answers the phone and gets the job done."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <Reveal key={step.title} delay={i * 0.08}>
                  <div className="relative flex h-full flex-col rounded-2xl border border-border bg-card p-8 shadow-soft">
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-secondary text-primary">
                      <Icon className="h-7 w-7" />
                    </div>
                    <span className="absolute right-7 top-7 font-display text-4xl font-bold text-muted-foreground/25">
                      0{i + 1}
                    </span>
                    <h3 className="mt-6 text-xl font-bold text-foreground">{step.title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{step.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== VETERAN BAND ===== */}
      <section className="relative overflow-hidden bg-brand-deep">
        <img
          src={business.logoMono}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-10 top-1/2 w-[420px] max-w-[60%] -translate-y-1/2 opacity-[0.07] mix-blend-screen sm:opacity-[0.09] lg:w-[520px]"
        />
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-10 py-16 container-px lg:grid-cols-2 lg:py-20">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-4 py-1.5 text-sm font-semibold text-white">
              <ShieldCheck className="h-4 w-4 text-accent" /> Service before, service still
            </span>
            <h2 className="mt-5 text-3xl font-bold text-white sm:text-4xl">
              Built on the values we served by
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-white/70">
              {business.legalName} is proudly Veteran owned. We understand showing up on time,
              doing honest work, and standing behind it &mdash; whether you&apos;re a fellow Veteran,
              a caregiver, or a neighbor who just needs a hand.
            </p>
            <div className="mt-8">
              <Button asChild variant="brand" size="lg">
                <Link to="/about">Our story <ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid grid-cols-2 gap-4">
              {[
                { k: "Veteran", v: "Owned & operated" },
                { k: "VA", v: "Veterans served" },
                { k: "8+", v: "Service categories" },
                { k: "12", v: "Trusted brands" },
              ].map((stat) => (
                <div key={stat.k} className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
                  <p className="font-display text-3xl font-bold text-primary">{stat.k}</p>
                  <p className="mt-1 text-sm text-white/70">{stat.v}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== BRANDS ===== */}
      <section className="section-py">
        <div className="mx-auto max-w-7xl container-px">
          <SectionHeading
            eyebrow="Manufacturer resources"
            title="The brands we deliver, service &amp; support"
            description="We work with the leading names in mobility. Explore each manufacturer's products, manuals and support resources."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {brands.map((b, i) => (
              <Reveal key={b.slug} delay={(i % 3) * 0.06}>
                <BrandCard brand={b} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICE AREA ===== */}
      <section className="bg-muted section-py">
        <div className="mx-auto grid max-w-7xl gap-12 container-px lg:grid-cols-2">
          <div>
            <SectionHeading
              align="left"
              eyebrow="Local coverage"
              title="Proudly serving Central PA &amp; the Tri-State region"
              description="If you're nearby and not sure you're in range, just call \u2014 we'll always do our best to help."
            />
            <div className="mt-8">
              <Button asChild variant="outlineDark" size="lg">
                <Link to="/service-area">View full service area <ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            {serviceRegions.map((region, i) => (
              <Reveal key={region.name} delay={i * 0.08}>
                <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-soft">
                  <h3 className="text-lg font-bold text-foreground">{region.name}</h3>
                  <p className="mt-1 text-sm text-accent">{region.note}</p>
                  <ul className="mt-4 space-y-2.5">
                    {region.areas.map((a) => (
                      <li key={a} className="flex items-start gap-2 text-[15px] text-muted-foreground">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="section-py">
        <div className="mx-auto max-w-7xl container-px">
          <SectionHeading
            eyebrow="From our customers"
            title="Trusted by families &amp; Veterans"
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <figure className="flex h-full flex-col rounded-2xl border border-border bg-card p-7 shadow-soft">
                  <div className="flex gap-1 text-primary">
                    {STAR_KEYS.map((k) => (
                      <Star key={k} className="h-5 w-5 fill-current" />
                    ))}
                  </div>
                  <blockquote className="mt-5 flex-1 text-[15px] leading-relaxed text-foreground/90">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-6 border-t border-border pt-4">
                    <p className="font-semibold text-foreground">{t.name}</p>
                    <p className="text-sm text-muted-foreground">{t.location}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="bg-muted section-py">
        <div className="mx-auto max-w-3xl container-px">
          <SectionHeading eyebrow="Questions" title="Frequently asked questions" />
          <Reveal className="mt-12">
            <Accordion type="single" collapsible className="w-full space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  className="overflow-hidden rounded-xl border border-border bg-card px-5"
                >
                  <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-brand-deep">
        <div className="mx-auto max-w-7xl py-16 container-px lg:py-24">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-yellow px-8 py-12 sm:px-14 sm:py-16">
            <div className="relative z-10 flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
              <div className="max-w-xl">
                <h2 className="text-3xl font-bold text-primary-foreground sm:text-4xl">
                  Got a mobility issue? Let&apos;s get it handled.
                </h2>
                <p className="mt-3 text-lg text-primary-foreground/80">
                  Call or text {business.owner} directly. Fast, honest help for any powered or
                  non-powered device.
                </p>
              </div>
              <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
                <Button asChild variant="dark" size="xl">
                  <a href={business.phoneHref}><Phone className="h-5 w-5" /> {business.phone}</a>
                </Button>
                <Button asChild variant="dark" size="xl" className="bg-secondary/0 border-2 border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground shadow-none">
                  <Link to="/contact">Request service</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
